import { useContext, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@tmh-website/ui/Button';
import CMSLink from '@tmh-website/components/shared/CMSLink';
import HubSpotFormRenderer, { HubSpotFormRendererProps } from '@tmh-website/components/shared/HubSpotFormRenderer';
import StyledTypo from '@tmh-website/styles/typo';
import { Color } from '@tmh-website/shared/colors';
import { GlobalContext } from '@tmh-website/shared';
import { isHubspotParameters } from '@tmh-website/services/form.service';
import { isValidLinkField } from '@tmh-website/shared/cms';
import { LocalePrefix, Project } from '@tmh-website/shared/const';

import * as Styled from './FooterSignup.styles';

interface FooterSignup {
  project: Project;
  isStandaloneMode: boolean;
  locale: LocalePrefix;
  title?: string;
  text?: string;
  link?: object;
}

const FooterSignup = ({ project, isStandaloneMode, locale, title, text, link, ...props }: FooterSignup) => {
  const globalContext = useContext(GlobalContext);
  const { t } = useTranslation();
  const { textOpacity } = useTheme();

  const [currentPath, setCurrentPath] = useState<HubSpotFormRendererProps['currentPath']>('');

  const updateCurrentPath = (): void => {
    const currentUrl: URL = new URL(window.location.href);
    setCurrentPath(currentUrl.pathname);
  };

  useEffect(() => {
    updateCurrentPath();

    window.addEventListener('popstate', updateCurrentPath);

    return () => {
      window.removeEventListener('popstate', updateCurrentPath);
    };
  }, []);

  if (!title && !text && !link) {
    return null;
  }

  if (locale === LocalePrefix.EnUS) {
    if (isHubspotParameters(globalContext.footerForm)) {
      return (
        <Styled.Container {...props}>
          <StyledTypo.Typography $style='h6' as='span'>
            {t('global:newsletter.label')}
          </StyledTypo.Typography>

          <HubSpotFormRenderer
            buttonHoverThemeColor={Color.Neutral_0_White}
            buttonThemeColor={Color.Blue_500_Ultramarine}
            currentPath={currentPath}
            formElementThemeColor={Color.Blue_500_Ultramarine}
            formParameters={globalContext.footerForm}
            isStaticSuccessMessageVisible={false}
            locale={locale}
            useNativeHubSpotEmbed={globalContext.footerForm.params.useNativeHubSpotEmbed}
          />
        </Styled.Container>
      );
    }
  } else {
    return (
      <div {...props}>
        {title && (
          <StyledTypo.Typography $style='h6' as='span'>
            {title}
          </StyledTypo.Typography>
        )}
        {text && (
          <Styled.Text
            input={text}
            typographyProps={{
              $style: 'uiText',
              $noMargin: true,
              $opacity: textOpacity,
              $color: Color.Neutral_0_White,
            }}
          />
        )}
        {link && isValidLinkField(link) && (locale === LocalePrefix.De || locale === LocalePrefix.DeCH) && (
          <CMSLink link={link} isStandaloneMode={isStandaloneMode} locale={locale}>
            <Button themeColor={Color.Blue_500_Ultramarine} hoverThemeColor={Color.Neutral_0_White}>
              {link.label}
            </Button>
          </CMSLink>
        )}
      </div>
    );
  }
};

export default FooterSignup;
